// Archive — every terminal decision the firm has made. CSV export wired.

import { useEffect, useMemo, useState } from "react";
import { fetchGraphSectors } from "../api";
import { useAppData } from "../store";
import type { Deal, SectorOverview } from "../types";
import {
  Avatar, Btn, Chip, Pill, ScoreGlyph, TextInput,
} from "../components/Primitives";
import { Logo } from "../components/Logo";

const ROW_COLS = "1.6fr 0.8fr 1fr 0.9fr 0.7fr 28px 96px";

const REASON_OF = (d: Deal) =>
  (d.reasonTags && d.reasonTags[0]) || d.reason || "—";

const csvEscape = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function exportCsv(rows: Deal[]) {
  const cols = ["co", "founders", "stage", "sector", "geo", "score",
                "status", "reason", "decidedBy", "decidedAt", "route"];
  const lines = [cols.join(",")];
  rows.forEach((r) => {
    lines.push(cols.map((c) => csvEscape((r as any)[c] ?? (c === "reason" ? REASON_OF(r) : ""))).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "prism-archive.csv"; a.click();
  URL.revokeObjectURL(url);
}

/** Entity-backed sector map (KG-4): what the graph knows per sector —
 *  alias-resolved, including companies that never pitched. */
function SectorMap({ onOpen }: { onOpen: (id: string) => void }) {
  const [sectors, setSectors] = useState<SectorOverview[] | null>(null);
  const [openSector, setOpenSector] = useState<string | null>(null);

  useEffect(() => {
    fetchGraphSectors().then(setSectors).catch(() => setSectors([]));
  }, []);

  if (!sectors || sectors.length === 0) return null;
  const open = sectors.find((s) => s.id === openSector);

  return (
    <div style={{ padding: "10px 22px 4px", borderBottom: "1px solid var(--border)" }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{
          fontSize: 10, fontWeight: 600, textTransform: "uppercase",
          letterSpacing: "0.08em", color: "var(--ink-3)", marginRight: 4,
        }}>
          Sector map
        </span>
        {sectors.map((s) => (
          <Chip key={s.id} on={openSector === s.id}
                onClick={() => setOpenSector(openSector === s.id ? null : s.id)}>
            {s.label} · {s.companies}
          </Chip>
        ))}
      </div>
      {open && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "8px 0 6px" }}>
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
            {open.dealBacked} pitched · {open.companies - open.dealBacked} known from the graph only:
          </span>
          {open.top.map((c, i) => (
            <span
              key={`${c.slug ?? c.co}-${i}`}
              onClick={c.slug ? () => onOpen(c.slug!) : undefined}
              title={c.slug ? "Open the deal" : "Known from founder histories / competitor mentions — never pitched"}
              style={{
                fontSize: 11.5, padding: "2px 9px", borderRadius: 10,
                background: c.slug ? "var(--accent-soft)" : "var(--raised)",
                border: "1px solid var(--border)",
                color: c.slug ? "var(--accent)" : "var(--ink-3)",
                cursor: c.slug ? "pointer" : "default",
              }}
            >
              {c.co}
            </span>
          ))}
          {open.companies > open.top.length && (
            <span style={{ fontSize: 11, color: "var(--ink-4)" }}>
              +{open.companies - open.top.length} more
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface Props { onOpen: (id: string) => void; }

export function ArchiveScreen({ onOpen }: Props) {
  const { data } = useAppData();
  const [outcome, setOutcome] = useState<"all" | "invested" | "passed">("all");
  const [search, setSearch] = useState("");
  const archive = data?.archive ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return archive.filter((d) => {
      if (outcome === "passed" && d.status !== "passed" && d.status !== "rejected") return false;
      if (outcome === "invested" && d.status !== "invested") return false;
      if (q && !d.co.toLowerCase().includes(q)
            && !(d.founders || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [archive, outcome, search]);

  return (
    <div className="page">
      <div className="topbar">
        <h1>Archive</h1>
        <span className="crumb">· {archive.length} decisions</span>
        <span className="grow" />
        <Btn ico="ext" size="sm" onClick={() => exportCsv(filtered)}>Export CSV</Btn>
        <TextInput search ico="search" placeholder="Search archive"
                   value={search} onChange={setSearch} style={{ width: 240 }} />
      </div>

      <div className="filter-bar">
        {(["all", "invested", "passed"] as const).map((k) => (
          <Chip key={k} on={outcome === k} onClick={() => setOutcome(k)}>
            {k === "all" ? "All outcomes" : k[0].toUpperCase() + k.slice(1)}
          </Chip>
        ))}
        <span className="grow" />
      </div>

      <SectorMap onOpen={onOpen} />

      <div className="scroll" style={{ background: "var(--surface)" }}>
        <div className="row-head" style={{ ["--row-cols" as any]: ROW_COLS }}>
          <span>Company</span>
          <span>Stage</span>
          <span>Reason</span>
          <span>Decided</span>
          <span>Outcome</span>
          <span>By</span>
          <span className="r">Score</span>
        </div>
        {filtered.length === 0 ? (
          <div className="empty">
            <h3>No decisions yet</h3>
            <p>Decisions made on Inbox deals show up here automatically.</p>
          </div>
        ) : filtered.map((d) => (
          <div
            key={d.id} className="row"
            style={{ ["--row-cols" as any]: ROW_COLS }}
            onClick={() => onOpen(d.id)}
          >
            <div className="row-co" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
              <Logo domain={d.domain} co={d.co} size={28} />
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                <span className="co">{d.co}</span>
                <span className="meta">{d.founders} · {d.route}</span>
              </div>
            </div>
            <span className="row-cell">{d.stage}</span>
            <span className="row-cell" style={{ color: REASON_OF(d) === "—" ? "var(--ink-4)" : "var(--ink-2)" }}>
              {REASON_OF(d)}
            </span>
            <span className="row-cell">{d.time}</span>
            <Pill kind={d.status} />
            <Avatar id={d.decidedBy ?? d.owner} />
            <div className="row-score">
              <ScoreGlyph value={d.score} />
              <span className="score num">{d.score ?? "—"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
